import React from "react";
import Html from "react-pdf-html";
import dayjs from "dayjs";
import Markdown from "../../Shared/Markdown";
import ReactDOMServer from "react-dom/server";
import { toTitleCase } from "shared/@utils/formatString";
import mapPdfVoteIcon from "../../AgendaPage/AgendaPdf/mapPdfVoteIcon";

import MyriadProLight from "./pdfFonts/MyriadPro-Light.woff";
import MyriadProRegular from "./pdfFonts/MYRIADPRO-REGULAR.woff";
import MyriadProLightItalic from "./pdfFonts/Myriad Pro Light Italic.otf";
import MyriadProRegularItalic from "./pdfFonts/Myriad Pro Italic.ttf";
import { ReactComponent as SaveActionPdfButton } from "../../assets/Edit_Category_Save_Action_Pdf.svg";

import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  PDFViewer,
  BlobProvider,
} from "@react-pdf/renderer";

Font.register({
  family: "Myriad Pro",
  fonts: [
    {
      src: MyriadProRegular,
      fontWeight: "bold",
    },
    {
      src: MyriadProLight,
      fontWeight: "light",
    },
    {
      src: MyriadProRegularItalic,
      fontWeight: "bold",
      fontStyle: "italic",
    },
    {
      src: MyriadProLightItalic,
      fontWeight: "light",
      fontStyle: "italic",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: "36px",
    fontFamily: "Myriad Pro",
    fontWeight: "light",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  lineStroke: {
    display: "flex",
    flexDirection: "row",
    height: "100%",
    width: "16px",
    justifyContent: "flex-start",
    position: "absolute",
  },
  date: {
    color: "#243746",
    fontSize: "17.5px",
    fontWeight: "bold",
  },
  title: {
    color: "#415364",
    marginTop: "8px",
    fontSize: "24px",
    fontWeight: "bold",
  },
  bold: {
    fontWeight: "bold",
  },
  light: {
    fontWeight: "light",
  },
  descriptionBoxContainer: {
    position: "relative",
  },
  descriptionBox: {
    color: "#415364",
    display: "flex",
    flexDirection: "column",
    marginLeft: "20px",
    whiteSpace: "pre-line",
    fontSize: "12px",
    maxWidth: "180px",
  },
  noteBox: {
    color: "#415364",
    display: "flex",
    flexDirection: "column",
    whiteSpace: "pre-line",
    fontSize: "12px",
    maxWidth: "200px",
  },
  footer: {
    position: "absolute",
    fontSize: "8px",
    bottom: 0,
    width: "100%",
    display: "flex",
    maxHeight: "8px",
    textAlign: "start",
  },
  body: {
    width: "100%",
    flex: 1,
    position: "relative",
  },
  tableHeader: {
    borderBottom: "1px solid #415364",
    borderTop: "1px solid #415364",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "6px 0px 5px 0px",
    margin: "8px 0px 2px 0px",
  },
  tableHeaderText: {
    color: "#243746",
    fontSize: "9px",
    fontStyle: "italic",
  },
  tableHeaderText1: {
    marginLeft: "20px",
  },
  tableHeaderText2: {
    marginLeft: "2px",
  },
  row: {
    padding: "0px 0px 5px 0px",
    display: "flex",
    margin: "0px",
    flexDirection: "row",
  },
  cell1: {
    flex: "50",
    width: "100%",
    height: "100%",
  },
  cell2: {
    flex: "35",
    width: "100%",
    height: "100%",
    padding: "0px 30px 0px 0px",
  },
  cell3: {
    flex: "15",
    width: "100%",
    height: "100%",
  },
  cell: {
    margin: "5px 0px",
  },
  iconText: {
    fontSize: "7px",
    fontWeight: "bold",
  },
});

const htmlStyles = {
  body: {
    whiteSpace: "pre-line",
  },
  p: {
    margin: 0,
    padding: 0,
    marginBottom: "5px",
  },
  li: {
    margin: 0,
    padding: 0,
  },
  ul: {
    margin: 0,
    padding: 0,
    marginLeft: "-17px",
  },
  ol: {
    margin: 0,
    padding: 0,
    marginLeft: "-17px",
  },
  article: {
    marginBottom: "-5px",
  },
};

export function ActionItemsPdf({ date, actionItems, isTest }) {
  const doc = actionItems && actionItems.length > 0 && myDoc(date, actionItems);

  return (
    doc &&
    (isTest ? (
      <PDFViewer width={1200} height={800}>
        {doc}
      </PDFViewer>
    ) : (
      <BlobProvider document={doc}>
        {({ loading, url }) =>
          loading ? (
            <div style={{ cursor: "progress" }}>
              <SaveActionPdfButton />
            </div>
          ) : (
            <a href={url} target="_blank">
              <SaveActionPdfButton />
            </a>
          )
        }
      </BlobProvider>
    ))
  );
}

const myDoc = (date, actions) => (
  <Document>
    <Page size="A4" style={styles.page} width={1000} height={1000}>
      <View style={[styles.header]}>
        <View>
          <Text style={[styles.date]}>{date}</Text>
        </View>
        <View>
          <Text style={[styles.title]}>Management Team Action Items</Text>
        </View>
      </View>
      <View style={styles.tableHeader}>
        <View style={styles.cell1}>
          <Text style={[styles.tableHeaderText, styles.tableHeaderText1]}>
            Agenda Item
          </Text>
        </View>
        <View style={styles.cell2}>
          <Text style={[styles.tableHeaderText, styles.tableHeaderText2]}>
            Notes
          </Text>
        </View>
        <View style={styles.cell3}>
          <Text style={styles.tableHeaderText}>Vote Result</Text>
        </View>
      </View>
      <View style={styles.body}>
        {actions.map((item, index) => {
          let leftBoxColor;

          switch (item.assignedType) {
            case "Diamond":
              leftBoxColor = "#7F6A45";
              break;
            case "WWG":
              leftBoxColor = "#91B6BB";
              break;
            case "Attorney":
              leftBoxColor = "#243746";
              break;
            default:
              break;
          }
          const htmlDescription = ReactDOMServer.renderToStaticMarkup(
            <Markdown content={item.description} />
          );

          return (
            <View style={[styles.actionItem, styles.row]} wrap={false}>
              <View
                style={[
                  styles.cell1,
                  styles.cell,
                  styles.descriptionBoxContainer,
                ]}
              >
                <View
                  style={[styles.lineStroke, { backgroundColor: leftBoxColor }]}
                />
                <View style={[styles.descriptionBox]}>
                  <Text style={styles.bold}>{toTitleCase(item.assignee)}</Text>
                  <Text style={styles.light}>{item.title}</Text>
                  {item.description && (
                    <Html style={{ fontSize: "12px" }} stylesheet={htmlStyles}>
                      {htmlDescription}
                    </Html>
                  )}
                </View>
              </View>
              <View style={[styles.cell2, styles.cell, styles.noteBox]}>
                {item.notes &&
                  item.notes.length > 0 &&
                  item.notes.map(
                    (a, index) =>
                      a &&
                      a.note && (
                        <Html
                          key={index}
                          style={{ fontSize: "12px" }}
                          stylesheet={htmlStyles}
                        >
                          {ReactDOMServer.renderToStaticMarkup(
                            <Markdown key={index} content={a.note} />
                          )}
                        </Html>
                      )
                  )}
              </View>
              <View style={[styles.cell3, styles.cell]}>
                {item?.votingStatus && mapPdfVoteIcon(item.votingStatus)}
              </View>
            </View>
          );
        })}
        <View style={styles.footer} fixed>
          <Text>
            {`This document is confidential and subject to attorney client privilege. For WWG™ Use Only. | Revised ${nowDate()} | CR# Not Reviewed`}
          </Text>
        </View>
      </View>
    </Page>
  </Document>
);

const nowDate = () => dayjs().format("MMMM DD, YYYY");
